import type { ReactNode } from "react";
import { useState, useMemo, useEffect } from "react";
import { Stack, Select, Text, Center, Anchor, TextInput, CopyButton, ActionIcon, Tooltip } from "@mantine/core";
import { FormattedMessage, useIntl } from "react-intl";
import { CodeHighlight } from "@mantine/code-highlight";
import { MapLibreMap } from "@mapka/react-maplibre-map";
import { Map as MapkaMap } from "@mapka/maplibre-gl-sdk";
import { useTRPC } from "../../../../trpc";
import { AsideModal } from "../../../../components/AsideModal";
import { useQuery } from "@tanstack/react-query";
import { useDefaultApiKey } from "../../../../hooks/apiKey";
import { Tabs } from "@mantine/core";
import { IconCheck, IconCopy, IconFrame, IconLayoutCollage, IconQrcode, IconVector } from "@tabler/icons-react";
import { API_HOST } from "../../../../constants/url";
import { QRCode } from "../../../../components/QRCode";
import type { MapkaStyleSpecification } from "@mapka/maplibre-gl-sdk";
import { captureEvent } from "../../../../utils/posthog";
import { Event } from "../../../../constants/events";

interface ShareMapModalProps {
  opened: boolean;
  onClose: () => void;
  accountName: string;
  style: {
    key: string;
    displayName: string;
    scope: "private" | "public";
  };
  version: {
    id: number;
    spec: MapkaStyleSpecification;
  };
}

function trimBy(text: string, x: number) {
  return text
    .split("\n")
    .map(line => line.slice(x))
    .join("\n")
    .trim();
}

export function ShareMapModal({ opened, onClose, style, version, accountName }: ShareMapModalProps) {
  const trpc = useTRPC();
  const intl = useIntl();
  const defaultApiKey = useDefaultApiKey();
  const {
    spec,
  } = version;

  const {
    key: styleKey,
  } = style;

  const [selectedApiKey, setSelectedApiKey] = useState(defaultApiKey);
  const { data: apiKeys = [] } = useQuery(trpc.getPublicAPIKeys.queryOptions());

  useEffect(() => {
    if (opened) {
      captureEvent(Event.ShareMapModalOpened);
    }
  }, [opened]);

  const apiKeyOptions = useMemo(() => {
    return apiKeys.map(({ key, name }) => ({
      value: key,
      label: name,
    }));
  }, [apiKeys]);

  const iframeUrl = useMemo(() => {
    return `https://${API_HOST}/maps/${accountName}/${styleKey}?apiKey=${selectedApiKey}`;
  }, [styleKey, selectedApiKey, accountName]);

  const iframeCode = useMemo(() => {
    return trimBy(`
      <iframe
        src="${iframeUrl}"
        width="800px"
        height="600px"
        style="border: none;"
      />`, 6);
  }, [iframeUrl]);

  const styleLink = useMemo(() => {
    return `https://${API_HOST}/v1/${accountName}/styles/${styleKey}.json?apiKey=${selectedApiKey}`;
  }, [styleKey, selectedApiKey, accountName]);

  const maplibreCode = useMemo(() => {
    return trimBy(`
      import 'maplibre-gl/dist/maplibre-gl.css';
      import * as maplibregl from 'maplibre-gl';

      const map = new maplibregl.Map({
        container: 'map',
        style: "${styleLink}",
        center: [0, 0],
        zoom: 2,
      });`, 6);
  }, [styleLink]);

  const styleURL = useMemo(() => {
    return `https://${API_HOST}/v1/${accountName}/styles/${styleKey}.json`;
  }, [styleKey, accountName]);

  const installSDKCode = useMemo(() => {
    return trimBy(`
      npm install @mapka/maplibre-gl-sdk
      # or
      yarn add @mapka/maplibre-gl-sdk`, 6);
  }, []);

  const sdkCode = useMemo(() => {
    return trimBy(`
      import "@mapka/maplibre-gl-sdk/styles.css";
      import { Map } from "@mapka/maplibre-gl-sdk";

      const map = new Map({
        apiKey: "${selectedApiKey}",
        container: 'map',
        style: "${styleURL}",
        center: [0, 0],
        zoom: 2,
      });`, 6);
  }, [styleURL, selectedApiKey]);

  return (

    <AsideModal
      opened={opened}
      onClose={onClose}
      asideRatio={50}
      title={intl.formatMessage({ defaultMessage: "Share `{styleKey}` map", id: "title.shareMapTitle" }, { styleKey })}
      size="100rem"
      asideVisibleFrom="sm"
      aside={(
        <MapLibreMap
          BaseMap={MapkaMap}
          height="900px"
          width="100%"
          apiKey={selectedApiKey}
          style={spec}
        />
      )}
    >
      <Stack>
        <Select
          data={apiKeyOptions}
          label={intl.formatMessage({ defaultMessage: "API Key", id: "label.apiKey" })}
          placeholder={intl.formatMessage({ defaultMessage: "Select API Key", id: "label.selectApiKey" })}
          value={selectedApiKey}
          onChange={(selectedKey: string | null) => {
            if (!selectedKey) {
              setSelectedApiKey(defaultApiKey);
            }
            else {
              setSelectedApiKey(selectedKey);
            }
          }}
        />

        <TextInput
          readOnly
          label={intl.formatMessage({ defaultMessage: "Map URL", id: "label.shareMapUrl" })}
          description={intl.formatMessage({
            defaultMessage: "Share this link to let anyone view your map in the browser.",
            id: "text.shareMapUrlDescription",
          })}
          value={iframeUrl}
          onClick={(e) => { e.currentTarget.select(); }}
          rightSectionWidth={42}
          rightSection={(
            <CopyButton value={iframeUrl} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied
                    ? intl.formatMessage({ defaultMessage: "Copied", id: "label.copied" })
                    : intl.formatMessage({ defaultMessage: "Copy", id: "label.copy" })}
                  withArrow
                  position="right"
                >
                  <ActionIcon
                    color={copied ? "teal" : "gray"}
                    variant="subtle"
                    onClick={() => {
                      copy();
                      captureEvent(Event.ShareMapUrlCopied);
                    }}
                  >
                    {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          )}
        />

        <Tabs
          defaultValue="iframe"
          variant="outline"
          onChange={(tab) => {
            if (tab) {
              captureEvent(Event.ShareMapTabChanged);
            }
          }}
        >
          <Tabs.List display="flex">
            <Tabs.Tab flex={1} value="iframe" leftSection={<IconFrame size={12} />}>
              IFrame
            </Tabs.Tab>
            <Tabs.Tab flex={1} value="qrcode" leftSection={<IconQrcode size={12} />}>
              QR Code
            </Tabs.Tab>
            <Tabs.Tab flex={1} value="maplibre-sdk" leftSection={<IconLayoutCollage size={12} />}>
              Mapka SDK
            </Tabs.Tab>
            <Tabs.Tab flex={1} value="maplibre" leftSection={<IconVector size={12} />}>
              MapLibre GL
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="iframe">
            <Stack pt="sm">
              <Text size="sm">
                <FormattedMessage
                  id="text.shareMapDescription"
                  defaultMessage="Add an interactive map to your website. Copy the code below and paste it into your HTML — no setup needed."
                />
              </Text>
              <CodeHighlight code={iframeCode} language="html" radius="md" />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="qrcode">
            <Stack pt="sm">
              <Text size="sm">
                <FormattedMessage
                  id="text.shareMapQrCodeDescription"
                  defaultMessage="Save this QR code and add it to posters, flyers, or slides. Anyone who scans it will open your map on their phone."
                />
              </Text>
              <Center>
                <QRCode url={iframeUrl} />
              </Center>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="maplibre">
            <Stack pt="sm">
              <Text size="sm">
                <FormattedMessage
                  id="text.shareMaplibreDescription"
                  defaultMessage="Use this if your project already uses <maplibreLink>MapLibre GL JS</maplibreLink>. For Mapka-specific features like custom markers and popups, use the Mapka SDK tab instead."
                  values={{
                    maplibreLink: (chunks: ReactNode) => (
                      <Anchor underline="always" href="https://maplibre.org/maplibre-gl-js/docs/" target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </Anchor>
                    ),
                  }}
                />
              </Text>
              <CodeHighlight code={maplibreCode} language="tsx" radius="md" />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="maplibre-sdk">
            <Stack pt="sm">
              <Text size="sm">
                <FormattedMessage
                  id="text.shareMapSDKDescription"
                  defaultMessage="The recommended way to add a Mapka map to your app. Includes popups, markers, and built-in API key handling. See the <mapkaSDKLink>SDK docs</mapkaSDKLink> for more."
                  values={{
                    mapkaSDKLink: (chunks: ReactNode) => (
                      <Anchor underline="always" href="https://github.com/mapka-dev/sdk" target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </Anchor>
                    ),
                  }}
                />
              </Text>

              <Text size="sm" fw={500}>
                <FormattedMessage
                  id="text.shareMapSDKInstallDescription"
                  defaultMessage="Install the SDK:"
                />
              </Text>
              <CodeHighlight code={installSDKCode} language="bash" radius="md" />

              <Text size="sm" fw={500}>
                <FormattedMessage
                  id="text.shareMapSDKUsageDescription"
                  defaultMessage="Initialize the map:"
                />
              </Text>
              <CodeHighlight code={sdkCode} language="tsx" radius="md" />
            </Stack>
          </Tabs.Panel>
        </Tabs>

      </Stack>
    </AsideModal>
  );
}
